"use client";

import { useRef, useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Button } from "../ui/button";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { submitCareerApplicationAjax } from "@/utils/actions/applications";
import { applyRequiredFieldErrors } from "@/utils/forms/requiredFieldHelpers";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "../ui/form";
import { Spinner } from "@/components/ui/shadcn-io/spinner";

interface CareerApplicationFormProps {
  jobOptions: { value: string; label: string }[];
}

const MAX_CV_SIZE = 5 * 1024 * 1024;

// Make CV optional at schema level so RHF doesn't treat empty initial state as an immediate fatal error.
// We'll enforce presence before submission.
const cvSchema = z
  .any()
  .optional()
  .refine(
    (file) =>
      !file ||
      (file instanceof File &&
        file.size > 0 &&
        file.type === "application/pdf" &&
        file.size <= MAX_CV_SIZE),
    {
      message: "Allega il CV in formato PDF (max 5MB)",
    }
  );

const careerApplicationSchema = z.object({
  name: z.string().trim().min(1, "Nome obbligatorio"),
  surname: z.string().trim().min(1, "Cognome obbligatorio"),
  email: z.string().trim().email("Email non valida"),
  phone: z.string().trim().min(5, "Telefono obbligatorio"),
  city: z.string().trim().min(1, "Città obbligatoria"),
  job: z.string().trim().min(1, "Seleziona una posizione"),
  message: z
    .string()
    .trim()
    .min(20, "Raccontaci qualcosa in almeno 20 caratteri"),
  cv: cvSchema,
  privacyAccepted: z
    .boolean()
    .refine((value) => value, { message: "Devi accettare la privacy" }),
});

type CareerApplicationFormValues = z.infer<typeof careerApplicationSchema>;

export function CareerApplicationForm({
  jobOptions,
}: Readonly<CareerApplicationFormProps>) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  const form = useForm<CareerApplicationFormValues>({
    resolver: zodResolver(careerApplicationSchema),
    defaultValues: {
      name: "",
      surname: "",
      email: "",
      phone: "",
      city: "",
      job: "",
      message: "",
      cv: undefined as unknown as File,
      privacyAccepted: false,
    },
    mode: "onSubmit",
  });

  // Unified missing required logic (no toast)
  const applyMissingRequired = (vals: CareerApplicationFormValues) =>
    applyRequiredFieldErrors(form, vals, [
      { key: "name", message: "Nome obbligatorio", isMissing: !vals.name },
      {
        key: "surname",
        message: "Cognome obbligatorio",
        isMissing: !vals.surname,
      },
      { key: "email", message: "Email non valida", isMissing: !vals.email },
      {
        key: "phone",
        message: "Telefono obbligatorio",
        isMissing: !vals.phone,
      },
      { key: "city", message: "Città obbligatoria", isMissing: !vals.city },
      { key: "job", message: "Seleziona una posizione", isMissing: !vals.job },
      {
        key: "message",
        message: "Raccontaci qualcosa in almeno 20 caratteri",
        isMissing:
          !vals.message ||
          (typeof vals.message === "string" && vals.message.trim().length < 20),
      },
      {
        key: "privacyAccepted",
        message: "Devi accettare la privacy",
        isMissing: !vals.privacyAccepted,
      },
    ]);

  const onValid = async (values: CareerApplicationFormValues) => {
    // Manual preflight validation for CV presence & privacy (schema allows optional to avoid blocking UX early)
    // Defensive check: ensure message meets minimum length before attempting submit
    if (!values.message || values.message.trim().length < 20) {
      form.setError("message" as any, {
        type: "manual",
        message: "Raccontaci qualcosa in almeno 20 caratteri",
      });
      return;
    }
    if (!values.cv || !(values.cv instanceof File) || values.cv.size === 0) {
      toast.error("Compilazione incompleta", {
        description: "Allega il CV in formato PDF",
      });
      return;
    }
    if (values.cv.type !== "application/pdf") {
      toast.error("Formato non valido", {
        description: "Il CV deve essere un PDF",
      });
      return;
    }
    if (values.cv.size > MAX_CV_SIZE) {
      toast.error("File troppo grande", { description: "Max 5MB" });
      return;
    }
    try {
      const formData = new FormData();
      formData.append("name", values.name);
      formData.append("surname", values.surname);
      formData.append("email", values.email);
      formData.append("phone", values.phone);
      formData.append("city", values.city);
      formData.append("job", values.job);
      formData.append("message", values.message);
      formData.append("privacyAccepted", values.privacyAccepted ? "on" : "");
      formData.append("cv", values.cv);

      const result = await submitCareerApplicationAjax(formData);
      if (result.success) {
        toast.success("Candidatura inviata", {
          description: "La tua candidatura è stata inviata con successo",
        });
        form.reset({
          name: "",
          surname: "",
          email: "",
          phone: "",
          city: "",
          // Reset job to undefined so SelectValue renders the placeholder
          job: undefined as unknown as string,
          message: "",
          cv: undefined as unknown as File,
          privacyAccepted: false,
        });
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        setFileName(null);
      } else {
        toast.error("Candidatura non inviata", {
          description: result.error || "Si è verificato un errore",
        });
      }
    } catch (error) {
      toast.error("Candidatura non inviata", {
        description:
          error instanceof Error
            ? error.message
            : "Impossibile inviare la candidatura. Riprova.",
      });
    }
  };

  const onSubmit = async (e?: React.BaseSyntheticEvent) => {
    try {
      if (e?.preventDefault) e.preventDefault();
      const vals = form.getValues() as CareerApplicationFormValues;
      const missingCount = applyMissingRequired(vals);
      if (missingCount > 0) return; // rely on inline red errors only

      // Run Zod validation manually to avoid resolver throwing to console
      // (some environments surface resolver errors as uncaught). Map issues
      // to RHF inline errors so the UI shows them without a console ZodError.
      const parsed = careerApplicationSchema.safeParse(vals);
      if (!parsed.success) {
        // set errors for each issue
        for (const issue of parsed.error.issues) {
          const key = issue.path[0] as
            | keyof CareerApplicationFormValues
            | undefined;
          if (key) {
            form.setError(key as any, {
              type: "manual",
              message: issue.message,
            });
          }
        }
        return;
      }

      // Finally submit validated values.
      await form.handleSubmit(onValid)();
    } catch (err) {
      // If a ZodError somehow bubbles up, convert to inline errors instead of crashing.
      if (err instanceof Error) {
        // eslint-disable-next-line no-console
        console.error("Error during form submit:", err);
      }
      toast.error(
        "Errore durante l'invio del form. Controlla i campi e riprova."
      );
    }
  };

  const isSubmitting = form.formState.isSubmitting;

  return (
    <div className="max-w-4xl mx-auto">
      <Card className="border-0 shadow-2xl">
        <CardHeader className="bg-gray-900 text-white text-center py-8">
          <CardTitle className="text-3xl lg:text-4xl mb-4">
            Invia la Tua Candidatura
          </CardTitle>
          <p className="text-xl opacity-90">
            Cerchiamo tecnici qualificati e persone motivate per unirsi al
            nostro team
          </p>
        </CardHeader>
        <CardContent className="p-8 space-y-6">
          <Form {...form}>
            <form className="space-y-6" onSubmit={onSubmit} noValidate>
              <div className="grid md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          placeholder="Nome *"
                          className="text-lg py-3 border border-gray-300 rounded-md bg-white"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="surname"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          placeholder="Cognome *"
                          className="text-lg py-3 border border-gray-300 rounded-md bg-white"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="Email *"
                          className="text-lg py-3 border border-gray-300 rounded-md bg-white"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          type="tel"
                          placeholder="Telefono *"
                          className="text-lg py-3 border border-gray-300 rounded-md bg-white"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        placeholder="Città di residenza *"
                        className="text-lg py-3 border border-gray-300 rounded-md bg-white"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="job"
                render={({ field }) => (
                  <FormItem>
                    <Select
                      value={field.value || undefined}
                      onValueChange={(value) => field.onChange(value)}
                    >
                      <FormControl>
                        <SelectTrigger className="text-sm py-3 border border-gray-300 rounded-md bg-white">
                          <SelectValue placeholder="Posizione di interesse" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="bg-white text-gray-900 border border-gray-300 shadow-lg z-50 max-h-64">
                        {jobOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="message"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Textarea
                        placeholder="Parlaci di te, della tua esperienza, delle tue motivazioni e di cosa ti aspetti da questa opportunità lavorativa..."
                        className="min-h-40 text-lg border border-gray-300 rounded-md bg-white"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="cv"
                render={({ field }) => (
                  <FormItem>
                    <label className="block text-lg font-medium text-gray-700">
                      Allega il tuo CV (formato PDF):
                    </label>
                    <FormControl>
                      <Input
                        id="cv-file"
                        type="file"
                        accept=".pdf"
                        ref={fileInputRef}
                        className="text-lg py-3 border border-gray-300 rounded-md bg-white"
                        onChange={(event) => {
                          const file = event.target.files?.item(0) ?? null;
                          field.onChange(file ?? undefined);
                          setFileName(file?.name ?? null);
                        }}
                        onBlur={field.onBlur}
                      />
                    </FormControl>
                    {fileName && (
                      <p className="text-sm text-gray-600 mt-1">{fileName}</p>
                    )}
                    <p className="text-sm text-gray-500">
                      Formato accettato: PDF (max 5MB)
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="privacyAccepted"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-start space-x-3">
                      <FormControl>
                        <input
                          type="checkbox"
                          id="privacy-job"
                          className="mt-1.5"
                          checked={field.value}
                          onChange={(event) =>
                            field.onChange(event.target.checked)
                          }
                        />
                      </FormControl>
                      <label
                        htmlFor="privacy-job"
                        className="text-gray-600 leading-relaxed"
                      >
                        Accetto il trattamento dei dati personali ai sensi del
                        GDPR per la selezione del personale. I dati saranno
                        utilizzati esclusivamente per finalità di recruiting e
                        cancellati al termine del processo selettivo. *
                      </label>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full !bg-gray-900 hover:!bg-black !text-white px-8 py-4 text-lg font-semibold rounded-lg transition-transform duration-150 shadow-lg hover:shadow-xl min-h-[56px] active:scale-[0.97]"
              >
                {isSubmitting ? (
                  <Spinner className="w-5 h-5 mr-3" />
                ) : (
                  <Send className="w-5 h-5 mr-3" />
                )}
                {isSubmitting ? "Invio in corso..." : "Invia Candidatura"}
              </Button>

              <div className="text-center text-gray-600">
                <p className="leading-relaxed">
                  Riceverai conferma della ricezione entro 24 ore.
                  <br />
                  Ti contatteremo entro 5 giorni lavorativi se il tuo profilo
                  risulterà interessante.
                </p>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
